import { useEffect } from "react";
import {
  TrendingUp,
  TrendingDown,
  Activity,
  Target,
  Percent,
  BarChart3,
} from "lucide-react";
import { useTradingStore } from "../hooks/useTradingStore";
import PriceChart from "../components/PriceChart";
import MetricCard from "../components/MetricCard";
import SpreadMonitor from "../components/SpreadMonitor";
import RecentTrades from "../components/RecentTrades";
import IndicatorPanel from "../components/IndicatorPanel";

export default function Dashboard() {
  const { metrics, currentBar, accountBalance, fetchMetrics } = useTradingStore();

  useEffect(() => {
    const controller = new AbortController()
    fetchMetrics(controller.signal)
    const interval = setInterval(() => fetchMetrics(controller.signal), 5000)
    return () => {
      controller.abort()
      clearInterval(interval)
    }
  }, [fetchMetrics]);

  const pnlColor = metrics.pnl >= 0 ? "positive" : "negative";
  const pnlIcon = metrics.pnl >= 0 ? TrendingUp : TrendingDown;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <div className="flex items-center gap-2 text-sm text-[#a0a0a0]">
          <Activity size={24} className="text-green-400 pulse" />
          <span>Live</span>
          <span className="text-[#666]">|</span>
          <span>
            {currentBar
              ? `Last: $${currentBar.close.toFixed(2)}`
              : "Waiting for data..."}
          </span>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="metrics-grid">
        <MetricCard
          title="Total P&L"
          value={`$${metrics.pnl.toLocaleString("en-US", {
            minimumFractionDigits: 2,
          })}`}
          icon={pnlIcon}
          variant={pnlColor}
          subtitle={
            accountBalance
              ? `${metrics.pnl >= 0 ? "+" : ""}${(
                  (metrics.pnl / accountBalance) *
                  100
                ).toFixed(2)}%`
              : undefined
          }
        />
        <MetricCard
          title="Realized P&L"
          value={`$${metrics.realized_pnl.toLocaleString("en-US", {
            minimumFractionDigits: 2,
          })}`}
          icon={Target}
          variant={metrics.realized_pnl >= 0 ? "positive" : "negative"}
        />
        <MetricCard
          title="Unrealized P&L"
          value={`$${metrics.unrealized_pnl.toLocaleString("en-US", {
            minimumFractionDigits: 2,
          })}`}
          icon={metrics.unrealized_pnl >= 0 ? TrendingUp : TrendingDown}
          variant={metrics.unrealized_pnl >= 0 ? "positive" : "negative"}
        />
        <MetricCard
          title="Win Rate"
          value={`${metrics.win_rate.toFixed(1)}%`}
          icon={Percent}
          subtitle={`${metrics.profitable_trades}/${metrics.total_trades} trades`}
        />
        <MetricCard
          title="Max Drawdown"
          value={
            metrics.max_drawdown == null
              ? "—"
              : `${metrics.max_drawdown.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                })} %`
          }
          icon={TrendingDown}
          variant="negative"
        />
        <MetricCard
          title="Sharpe Ratio"
          value={metrics.sharpe_ratio?.toFixed(2) || "N/A"}
          icon={BarChart3}
          variant={
            metrics.sharpe_ratio && metrics.sharpe_ratio > 1
              ? "positive"
              : "neutral"
          }
        />
        <MetricCard
          title="Profit Factor"
          value={metrics.profit_factor?.toFixed(2) || "N/A"}
          icon={Activity}
          variant={
            metrics.profit_factor && metrics.profit_factor > 1
              ? "positive"
              : "negative"
          }
        />
      </div>

      {/* Charts Grid */}
      <div className="chart-grid">
        {/* Main Price Chart */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Price Chart</h2>
          </div>
          <div className="h-[400px]">
            <PriceChart />
          </div>
        </div>

        {/* Side Panel */}
        <div className="space-y-4">
          {/* Spread Monitor */}
          <SpreadMonitor />

          {/* Indicator Panel */}
          <IndicatorPanel />

          {/* Recent Trades */}
          <RecentTrades />
        </div>
      </div>
    </div>
  );
}
